import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import CloudUploadIcon from "@mui/icons-material/CloudUploadRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineRounded";
import DescriptionIcon from "@mui/icons-material/DescriptionRounded";
import ErrorIcon from "@mui/icons-material/ErrorRounded";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFileRounded";
import ShieldIcon from "@mui/icons-material/ShieldRounded";
import WarningAmberIcon from "@mui/icons-material/WarningAmberRounded";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import StatusBadge from "../../components/dashboard/MediTrackDashboard/components/StatusBadge";
import { mapProfileReportsToMediTrackRows } from "../../components/dashboard/MediTrackDashboard/profileToMediTrack";
import { addProfile, deleteReport, getProfile } from "../../services/user.service";
import "./Upload.scss";

export default function Upload() {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [reportRows, setReportRows] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [listError, setListError] = useState("");
  const [deleteModalRow, setDeleteModalRow] = useState(null);
  const [deleteModalError, setDeleteModalError] = useState(null);
  const deleteCancelRef = useRef(null);

  const loadReports = useCallback(async () => {
    try {
      setListError("");
      setReportsLoading(true);
      const profile = await getProfile();
      const raw = profile?.reports || [];
      setReportRows(mapProfileReportsToMediTrackRows(raw));
    } catch (e) {
      setListError(e?.response?.data?.message || "Could not load your reports.");
      setReportRows([]);
    } finally {
      setReportsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const closeDeleteModal = useCallback(() => {
    if (deletingId) return;
    setDeleteModalRow(null);
    setDeleteModalError(null);
  }, [deletingId]);

  useEffect(() => {
    if (!deleteModalRow) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusId = requestAnimationFrame(() => deleteCancelRef.current?.focus());
    const onKey = (e) => {
      if (e.key === "Escape") closeDeleteModal();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(focusId);
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [deleteModalRow, closeDeleteModal]);

  const openDeleteModal = useCallback((row) => {
    if (!row?.id || deletingId) return;
    setDeleteModalError(null);
    setDeleteModalRow(row);
  }, [deletingId]);

  const confirmDeleteInModal = useCallback(async () => {
    const row = deleteModalRow;
    if (!row?.id) return;
    setDeleteModalError(null);
    try {
      setDeletingId(row.id);
      await deleteReport(row.id);
      setDeleteModalRow(null);
      await loadReports();
    } catch (err) {
      setDeleteModalError(err?.response?.data?.message || "Could not delete this report. Please try again.");
    } finally {
      setDeletingId(null);
    }
  }, [deleteModalRow, loadReports]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setStatus("idle");
      setMessage("");
    } else {
      setStatus("error");
      setMessage("Please upload a PDF health report.");
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== "application/pdf") {
        setStatus("error");
        setMessage("Please choose a PDF file.");
        return;
      }
      setFile(selectedFile);
      setStatus("idle");
      setMessage("");
    }
  };

  const resetPicker = () => {
    setFile(null);
    setStatus("idle");
    setMessage("");
  };

  const onUpload = async () => {
    if (!file) return;

    try {
      setStatus("uploading");
      setMessage("Uploading your health report…");

      setStatus("processing");
      setMessage("Extracting health metrics from your report…");
      const result = await addProfile(file);

      const count = result?.latestReport?.metricsFound ?? 0;
      setStatus("completed");
      if (count === 0) {
        setMessage(
          "Report saved, but no numeric values were detected. This often happens with scanned (image-only) PDFs — try downloading a “text” or “print to PDF” version from your lab portal, or export as PDF from email."
        );
      } else {
        setMessage(`Done! Extracted ${count} health metric${count !== 1 ? "s" : ""} from your report.`);
      }

      await loadReports();
    } catch (err) {
      setStatus("error");
      setMessage(err?.response?.data?.message || "Upload failed. Please try again.");
    }
  };

  const metricCount = (row) =>
    row.metrics && typeof row.metrics === "object" ? Object.keys(row.metrics).length : 0;

  return (
    <div className="upload-container">
      <div className="upload-header">
        <motion.div initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}>
          <div className="upload-header__eyebrow">
            <ShieldIcon sx={{ fontSize: 18 }} />
            <span>Secure upload</span>
          </div>
          <h1>Upload health reports</h1>
          <p>
            Add PDF lab results and we&apos;ll parse key values into your dashboard, timelines, and charts — encrypted in
            transit.
          </p>
          <div className="upload-header__links">
            <Link to="/dashboard" className="upload-header__link">
              View dashboard
            </Link>
            <span className="upload-header__dot" aria-hidden />
            <Link to="/timeline" className="upload-header__link">
              Timeline
            </Link>
            <span className="upload-header__dot" aria-hidden />
            {reportRows.length > 0 ? (
              <>
                <a href="#upload-reports-title" className="upload-header__link">
                  Your reports ({reportRows.length})
                </a>
                <span className="upload-header__dot" aria-hidden />
              </>
            ) : null}
            <span className="upload-header__hint">PDF · max size per your server limits</span>
          </div>
        </motion.div>
      </div>

      <div className="upload-content">
        <motion.div
          data-tour="upload-dropzone"
          className={`upload-box ${isDragging ? "upload-box--dragging" : ""} ${file ? "upload-box--has-file" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <AnimatePresence mode="wait">
            {!file ? (
              <motion.div
                key="empty"
                className="upload-box__empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="upload-box__icon-wrapper">
                  <CloudUploadIcon />
                </div>
                <h3>Drop a PDF here or browse</h3>
                <p>Lab reports, discharge summaries, and routine panels (PDF only)</p>
                <input type="file" id="fileInput" accept=".pdf,application/pdf" onChange={handleFileSelect} className="upload-box__input" />
                <label htmlFor="fileInput" className="upload-box__btn">
                  Select PDF
                </label>
              </motion.div>
            ) : (
              <motion.div
                key="file"
                className="upload-box__file"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="upload-box__file-info">
                  <InsertDriveFileIcon className="upload-box__file-icon" />
                  <div className="upload-box__file-details">
                    <span className="upload-box__file-name">{file.name}</span>
                    <span className="upload-box__file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                  </div>
                  <button type="button" className="upload-box__remove" onClick={resetPicker} aria-label="Remove file">
                    <CloseIcon />
                  </button>
                </div>

                <div className="upload-box__status-area">
                  {status === "idle" && (
                    <motion.button
                      type="button"
                      className="upload-box__submit-btn"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={onUpload}
                    >
                      <AutoAwesomeIcon />
                      Analyze &amp; save to profile
                    </motion.button>
                  )}

                  {(status === "uploading" || status === "processing") && (
                    <div className="upload-box__progress">
                      <div className="upload-box__spinner" />
                      <span>{message}</span>
                    </div>
                  )}

                  {status === "completed" && (
                    <div className="upload-box__success-block">
                      <div className="upload-box__success">
                        <CheckCircleIcon />
                        <span>{message}</span>
                      </div>
                      <div className="upload-box__success-actions">
                        <button type="button" className="upload-box__secondary-btn" onClick={resetPicker}>
                          Upload another
                        </button>
                        <Link to="/dashboard" className="upload-box__dashboard-link">
                          Open dashboard
                        </Link>
                      </div>
                    </div>
                  )}

                  {status === "error" && (
                    <div className="upload-box__error">
                      <ErrorIcon />
                      <span>{message}</span>
                      <button type="button" onClick={() => setStatus("idle")}>
                        Try again
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <div className="upload-info">
          <div className="upload-card">
            <h3>Why upload here?</h3>
            <ul className="upload-list">
              <li>
                <strong>Structured data:</strong> Values like HbA1c, lipids, and CBC flow into trends and comparisons.
              </li>
              <li>
                <strong>One timeline:</strong> Each PDF becomes a dated entry tied to your account.
              </li>
              <li>
                <strong>You are in control:</strong> Review everything below and remove any report you don&apos;t want stored.
              </li>
            </ul>
          </div>

          <div className="upload-card upload-card--privacy">
            <div className="upload-card__privacy-header">
              <CheckCircleIcon />
              <h3>Privacy</h3>
            </div>
            <p>Your files are sent over HTTPS and stored with your user profile. We don&apos;t sell health data.</p>
          </div>
        </div>
      </div>

      <section data-tour="upload-reports-list" className="upload-reports" aria-labelledby="upload-reports-title">
        <div className="upload-reports__head">
          <div>
            <h2 id="upload-reports-title" className="upload-reports__title">
              Your reports
            </h2>
            <p className="upload-reports__subtitle">
              Newest first. Deleting removes the file metadata and extracted metrics from your profile and refreshes charts.
            </p>
          </div>
          <button type="button" className="upload-reports__refresh" onClick={loadReports} disabled={reportsLoading}>
            {reportsLoading ? "Refreshing…" : "Refresh list"}
          </button>
        </div>

        {listError ? <p className="upload-reports__error">{listError}</p> : null}

        {reportsLoading && reportRows.length === 0 ? (
          <div className="upload-reports__skeleton" aria-hidden>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="upload-reports__skeleton-row" />
            ))}
          </div>
        ) : reportRows.length === 0 ? (
          <div className="upload-reports__empty">
            <DescriptionIcon className="upload-reports__empty-icon" />
            <p className="upload-reports__empty-title">No reports yet</p>
            <p className="upload-reports__empty-text">Upload a PDF above to see it listed here.</p>
          </div>
        ) : (
          <div className="upload-reports__table-wrap">
            <table className="upload-reports__table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>File</th>
                  <th>Type</th>
                  <th>Summary</th>
                  <th>Metrics</th>
                  <th>Status</th>
                  <th className="upload-reports__th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reportRows.map((row) => (
                  <tr key={row.id}>
                    <td className="upload-reports__cell-muted">{row.date}</td>
                    <td className="upload-reports__cell-strong" title={row.fileName}>
                      {row.fileName}
                    </td>
                    <td>
                      <span className="upload-reports__pill">{row.reportType || "general"}</span>
                    </td>
                    <td className="upload-reports__cell-clip">{row.keyResult}</td>
                    <td className="upload-reports__cell-num">{metricCount(row)}</td>
                    <td>
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="upload-reports__actions">
                      <button
                        type="button"
                        className="upload-reports__delete"
                        onClick={() => openDeleteModal(row)}
                        disabled={!!deletingId}
                        title="Delete report"
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                        <span>Delete</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence mode="wait">
            {deleteModalRow ? (
              <motion.div
                key="delete-report-overlay"
                className="fixed inset-0 z-[300] flex items-center justify-center p-4 sm:p-6"
                role="presentation"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <button
                  type="button"
                  aria-label="Close dialog"
                  className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
                  onClick={closeDeleteModal}
                />
                <motion.div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="delete-report-title"
                  aria-describedby="delete-report-desc"
                  className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_25px_50px_-12px_rgba(15,23,42,0.25)]"
                  initial={{ opacity: 0, scale: 0.96, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98, y: 8 }}
                  transition={{ type: "spring", damping: 28, stiffness: 320 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white px-6 pb-5 pt-6">
                    <div className="flex items-start gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-50 ring-1 ring-amber-200/80">
                        <WarningAmberIcon sx={{ fontSize: 28 }} className="text-amber-600" />
                      </div>
                      <div className="min-w-0 flex-1 pt-0.5">
                        <h3 id="delete-report-title" className="text-lg font-semibold tracking-tight text-slate-900">
                          Delete this report?
                        </h3>
                        <p id="delete-report-desc" className="mt-2 text-sm leading-relaxed text-slate-600">
                          This removes{" "}
                          <span className="font-semibold text-slate-900">
                            {deleteModalRow.fileName || deleteModalRow.testName || "this file"}
                          </span>{" "}
                          and its extracted metrics from your profile. Charts and summaries will update. This cannot be
                          undone.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-4">
                    {deleteModalError ? (
                      <div
                        className="mb-4 flex gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800"
                        role="alert"
                      >
                        <ErrorIcon className="shrink-0 text-red-600" sx={{ fontSize: 20 }} />
                        <span>{deleteModalError}</span>
                      </div>
                    ) : null}

                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                      <button
                        ref={deleteCancelRef}
                        type="button"
                        className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-50"
                        onClick={closeDeleteModal}
                        disabled={!!deletingId}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="inline-flex h-11 items-center justify-center rounded-xl bg-red-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:pointer-events-none disabled:opacity-60"
                        onClick={confirmDeleteInModal}
                        disabled={!!deletingId}
                      >
                        {deletingId ? "Deleting…" : "Delete report"}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ) : null}
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
}
